import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ShoppingBag, ArrowRight } from 'lucide-react';
import axios from 'axios';


const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductImage = ({ src, alt, eager }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const url = src?.startsWith('/api/') ? `${API_URL}${src}` : src;
  return <div className="w-full h-full bg-gray-50 flex items-center justify-center">
    {url && !failed ? <img src={url} alt={alt} loading={eager ? 'eager' : 'lazy'} decoding="async" onError={() => setFailed(true)} className="w-full h-full object-contain" />
      : <span className="text-sm text-gray-500">Photo coming soon</span>}
  </div>;
};

/* ─── Reveal-on-scroll wrapper ─── */
const RevealCard = ({ children, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: '50px', threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const delay = (index % 3) * 80;

  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {children}
    </div>
  );
};

/* ─── Skeleton Card ─── */
const SkeletonCard = () => (
  <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
    <div className="aspect-[4/5] bg-gray-100 animate-pulse" />
    <div className="p-5 space-y-3">
      <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse" />
      <div className="h-3 bg-gray-100 rounded w-full animate-pulse" />
      <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse" />
    </div>
  </div>
);

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [error, setError] = useState('');

  const selectedCategory = searchParams.get('category');
  const collection = categories.find(c => c.slug === selectedCategory);

  useEffect(() => { fetchData(); }, [selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`, {
          params: { category_slug: selectedCategory, search: search || undefined, limit: 200 }
        }),
        axios.get(`${API_URL}/api/categories`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      setProducts([]); setError('Products could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchData(); };
  const handleCategoryClick = (slug) => slug ? setSearchParams({ category: slug }) : setSearchParams({});

  return (
    <div className="min-h-screen bg-white" data-testid="shop-page">
      {/* Hero */}
      <section className="relative py-10 bg-[#4A90A4] overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-white">{categories.find(c => c.slug === selectedCategory)?.name || 'Shop'}</h1>
          <p className="text-white/75 max-w-2xl">{collection?.description || 'Browse our collection of custom apparel, uniforms, and personalized items.'}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <form onSubmit={handleSearch} className="mb-8">
              <div className="relative">
                <input
                  type="text" placeholder="Search products..." value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-3 pl-10 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900"
                  data-testid="search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </form>
            <div>
              <h3 className="font-bold mb-4 text-gray-900">Categories</h3>
              <select aria-label="Shop category" value={selectedCategory || ''} onChange={e => handleCategoryClick(e.target.value)} className="lg:hidden w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">All Products</option>{categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
              <ul className="hidden lg:block space-y-1">
                <li>
                  <button onClick={() => handleCategoryClick(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${!selectedCategory ? 'bg-[#4A90A4]/10 text-[#4A90A4] font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
                    data-testid="category-all"
                  >All Products</button>
                </li>
                {categories.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => handleCategoryClick(c.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${selectedCategory === c.slug ? 'bg-[#4A90A4]/10 text-[#4A90A4] font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
                      data-testid={`category-${c.slug}`}
                    >{c.name}</button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {error && <div role="alert" className="mb-4 text-red-700">{error} <button className="underline p-2" onClick={fetchData}>Try again</button></div>}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(9)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingBag className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-4">No products found</p>
                <Link to="/quote" className="inline-flex items-center px-6 py-3 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow text-sm">
                  Request Custom Quote <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-400 mb-6" data-testid="product-count">
                  {products.length} product{products.length !== 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product, idx) => (
                    <RevealCard key={product.id} index={idx}>
                      <Link to={`/product/${product.slug}`}
                        className="product-card group rounded-2xl border border-gray-100 overflow-hidden bg-white block"
                        data-testid={`product-card-${product.slug}`}
                      >
                        <div className="aspect-[4/5] overflow-hidden bg-gray-50">
                          {product.primary_image ? (
                            <ProductImage
                              src={product.primary_image}
                              alt={product.name}
                              eager={idx < 6}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-5xl font-bold text-gray-200">{product.name.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-5">
                          <h3 className="font-bold mb-1 text-gray-900 group-hover:text-[#4A90A4] transition-colors">{product.name}</h3>
                          {product.short_description && <p className="text-sm text-gray-500 mb-2 line-clamp-2">{product.short_description}</p>}
                          <div className="flex items-center gap-2">
                            {product.sale_price ? (
                              <>
                                <span className="text-[#F5A623] font-bold">${product.sale_price.toFixed(2)}</span>
                                <span className="text-sm text-gray-400 line-through">${product.base_price.toFixed(2)}</span>
                              </>
                            ) : (
                              <span className="text-[#4A90A4] font-bold">${product.base_price.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    </RevealCard>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
